// src/context/SplitExpenseContext.js
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from './AuthContext';
import { ExpenseContext } from './ExpenseContext';
import { 
  createSplitExpense, 
  createParticipant, 
  updateParticipantStatus, 
  calculateSplitStatus 
} from '../models/SplitExpenseModel';
import { sendSplitExpenseEmail } from '../services/EmailService';
// Add Firebase imports
import { db } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  doc, 
  deleteDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs, 
  onSnapshot, 
  getDoc,
  setDoc
} from 'firebase/firestore';

const ensureStringId = (id) => {
  if (id === null || id === undefined) return null;
  return String(id);
};

export const SplitExpenseContext = createContext();

export const SplitExpenseProvider = ({ children }) => {
  const [splitExpenses, setSplitExpenses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailStatus, setEmailStatus] = useState({});
  
  // Add refs to track and clean up listeners
  const emailTimeoutsRef = useRef({});
  const contactsListenerRef = useRef(null);
  
  const { currentUser } = useContext(AuthContext);
  const { addExpense } = useContext(ExpenseContext);
  
  // Load data from Firestore when component mounts or user changes
  useEffect(() => {
    if (!currentUser) {
      setSplitExpenses([]);
      setContacts([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    console.log('Loading split expenses for user:', currentUser.uid);
    
    // Query Firestore for split expenses
    const splitExpensesQuery = query(
      collection(db, 'splitExpenses'),
      where('userId', '==', currentUser.uid)
    );
    
    // Set up real-time listener for split expenses
    const unsubscribe = onSnapshot(
      splitExpensesQuery, 
      (snapshot) => {
        try {
          // Process incoming data changes
          const splitExpensesList = snapshot.docs.map(doc => {
            const data = doc.data();
            // Ensure ID is always stored as a string
            return {
              id: String(doc.id),
              ...data,
              // Ensure participants have string IDs too
              participants: Array.isArray(data.participants) 
                ? data.participants.map(p => ({...p, id: String(p.id)})) 
                : data.participants
            };
          });
          
          console.log('Loaded split expenses:', splitExpensesList.length);
          setSplitExpenses(splitExpensesList);
          setLoading(false);
        } catch (error) {
          console.error("Error processing split expenses data:", error);
          setSplitExpenses([]);
          setLoading(false);
        }
      }, 
      (error) => {
        console.error("Error getting split expenses:", error.code, error.message);
        setLoading(false);
      }
    );
    
    // Load contacts
    loadContacts();
    
    return () => {
      unsubscribe();
      // Clean up contacts listener if it exists
      if (contactsListenerRef.current) {
        contactsListenerRef.current();
      }
    };
  }, [currentUser]);
  
  // Clean up email status timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts when component unmounts
      Object.values(emailTimeoutsRef.current).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      emailTimeoutsRef.current = {};
    };
  }, []);
  
  // Function to load contacts from Firestore - with improved error handling
  const loadContacts = async () => {
    if (!currentUser) {
      setContacts([]);
      return false;
    }
    
    try {
      console.log('Setting up contacts listener for user:', currentUser.uid);
      
      const contactsQuery = query(
        collection(db, 'contacts'),
        where('userId', '==', currentUser.uid)
      );
      
      // Set up real-time listener for contacts
      const unsubscribe = onSnapshot(
        contactsQuery,
        (snapshot) => {
          try {
            const contactsList = snapshot.docs.map(doc => ({
              id: String(doc.id),  // Ensure ID is stored as a string
              ...doc.data()
            }));
            
            console.log('Loaded contacts:', contactsList.length);
            setContacts(contactsList);
          } catch (error) {
            console.error('Error processing contacts data:', error);
            setContacts([]);
          }
        },
        (error) => {
          console.error('Error in contacts listener:', error);
          setContacts([]);
        }
      );
      
      // Store the unsubscribe function to clean up on unmount
      contactsListenerRef.current = unsubscribe;
      
      return true;
    } catch (error) {
      console.error('Error setting up contacts listener:', error);
      setContacts([]);
      return false;
    }
  };
  
  /**
   * Create a new split expense with improved error handling
   * @param {Object} expenseData - Expense data (amount, category, etc.)
   * @param {Array} participants - Array of participants
   * @returns {Object} New split expense or null if failed
   */
  const createNewSplitExpense = async (expenseData, participants) => {
    if (!currentUser) {
      console.error('Cannot create split expense: No authenticated user');
      return null;
    }
    
    try {
      console.log('Creating new split expense with:', { 
        expenseDetails: expenseData,
        participantCount: participants.length
      });
      
      // Ensure participants have string IDs and valid data
      const validatedParticipants = participants.map(p => ({
        ...p,
        id: String(p.id || `p_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`),
        shareAmount: parseFloat(p.shareAmount) || 0,
        sharePercentage: parseFloat(p.sharePercentage) || 0,
        status: p.status || 'unpaid'
      }));
      
      // Find the creator's participant object to determine their share
      const creatorParticipant = validatedParticipants.find(p => 
        (p.email && currentUser.email && p.email.toLowerCase() === currentUser.email.toLowerCase()) || 
        (p.name && currentUser.name && p.name === currentUser.name)
      );
      
      // If creator isn't found in participants, use the first participant as a fallback
      const userShare = creatorParticipant?.shareAmount || 
                       (validatedParticipants.length > 0 ? validatedParticipants[0].shareAmount : 0);
      
      // First create the base expense with proper split expense attribution
      const baseExpense = {
        ...expenseData,
        // Make sure amount is parsed as a number
        amount: parseFloat(expenseData.amount),
        // Mark this as a split expense
        isSplit: true,
        // Add the current user's share for proper budget calculations
        userShare: userShare,
        // Add the number of participants
        participantCount: validatedParticipants.length,
        // Add userId if not already set
        userId: currentUser.uid,
        // Add a descriptive note
        notes: expenseData.notes || `Split with ${validatedParticipants.length} people`,
        // Add creation date
        createdAt: new Date().toISOString()
      };
      
      console.log('Creating base expense for split:', baseExpense);
      
      // Try to add the expense
      const ensureStringId = await addExpense(baseExpense);
      
      if (!ensureStringId) {
        throw new Error('Failed to create base expense');
      }
      
      console.log('Base expense created with ID:', ensureStringId);
      
      // Create the split expense object
      const newSplitExpense = createSplitExpense(
        {
          ...expenseData,
          // The addExpense function returns the ID, but we need the full object
          id: ensureStringId,
          userId: currentUser.uid
        },
        validatedParticipants
      );
      
      console.log('Split expense object created:', newSplitExpense);
      
      // Add to Firestore
      const splitExpenseData = {
        ...newSplitExpense,
        userId: currentUser.uid,
        createdAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, 'splitExpenses'), splitExpenseData);
      console.log('Split expense added to Firestore with ID:', docRef.id);
      
      // Add the Firestore document ID
      const splitExpenseWithId = {
        ...splitExpenseData,
        id: String(docRef.id)
      };
      
      // Add any new contacts to Firestore
      const existingContactEmails = contacts
        .map(c => c.email?.toLowerCase())
        .filter(Boolean);
      
      console.log('Checking for new contacts to add...');
      
      for (const participant of validatedParticipants) {
        // Only add contacts with email addresses
        if (participant.email && 
            !existingContactEmails.includes(participant.email.toLowerCase())) {
          
          console.log('Adding new contact:', participant.name);
          
          try {
            // Create a new contact entry
            const contactData = {
              name: participant.name,
              email: participant.email,
              userId: currentUser.uid,
              createdAt: new Date().toISOString()
            };
            
            const contactRef = await addDoc(collection(db, 'contacts'), contactData);
            console.log('New contact added with ID:', contactRef.id);
            
            // Update local contacts state
            setContacts(prev => [...prev, {
              id: String(contactRef.id),
              ...contactData
            }]);
          } catch (contactError) {
            console.error('Error adding contact:', contactError);
            // Continue with split expense creation even if contact can't be added
          }
        }
      }
      
      // Send email notifications to participants (except the creator)
      const otherParticipants = validatedParticipants.filter(p => 
        p.email && currentUser.email && p.email.toLowerCase() !== currentUser.email.toLowerCase()
      );
      
      if (otherParticipants.length > 0) {
        console.log(`Sending split expense notifications to ${otherParticipants.length} participants`);
        
        // Create a queue to send emails sequentially to avoid rate limits
        const sendEmailQueue = async () => {
          for (let i = 0; i < otherParticipants.length; i++) {
            const participant = otherParticipants[i];
            const stringParticipantId = String(participant.id);
            
            try {
              // Track email status
              setEmailStatus(prev => ({
                ...prev,
                [stringParticipantId]: { sending: true }
              }));
              
              // Send the email with minimal parameters
              const result = await sendSplitExpenseEmail(
                participant, 
                splitExpenseWithId, 
                currentUser
              );
              
              console.log(`Email result for ${participant.name}:`, result);
              
              // Update email status
              setEmailStatus(prev => ({
                ...prev,
                [stringParticipantId]: { 
                  sending: false,
                  sent: result.success, 
                  error: result.success ? null : result.message 
                }
              }));
              
              // Wait a bit between emails to avoid rate limiting
              if (i < otherParticipants.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            } catch (error) {
              console.error(`Failed to send split email to ${participant.name}:`, error);
              
              // Update email status with error
              setEmailStatus(prev => ({
                ...prev,
                [stringParticipantId]: { 
                  sending: false,
                  sent: false, 
                  error: error.message || 'Unknown error' 
                }
              }));
              
              // Continue with the next participant even if this one fails
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
          
          // Clear email status after all emails have been processed (plus a buffer)
          const timeoutId = setTimeout(() => {
            setEmailStatus({});
          }, 10000);
          
          // Store timeout ID to clear if component unmounts
          emailTimeoutsRef.current['all'] = timeoutId;
        };
        
        // Start the email queue
        sendEmailQueue();
      }
      
      return splitExpenseWithId;
    } catch (error) {
      console.error('Error creating split expense:', error);
      return null;
    }
  };
  
  /**
   * Get a split expense by ID
   * @param {string|number} id - Split expense ID
   * @returns {Object} Split expense or null if not found
   */
  const getSplitExpense = (id) => {
    if (!id) return null;
    const stringId = ensureStringId(id);
    return splitExpenses.find(expense => ensureStringId(expense.id) === stringId) || null;
  };
  
  /**
   * Update a participant's payment status with improved error handling
   * @param {string|number} splitExpenseId - Split expense ID
   * @param {string} participantId - Participant ID
   * @param {string} status - New status (paid, unpaid, declined)
   * @param {string} paymentMethod - Method of payment
   * @returns {Promise<boolean>} Success status
   */
  const updatePaymentStatus = async (splitExpenseId, participantId, status, paymentMethod) => {
    if (!currentUser) {
      console.error('Cannot update payment status: No authenticated user');
      return false;
    }
    
    const stringExpenseId = String(splitExpenseId);
    const stringParticipantId = String(participantId);
    
    console.log(`Starting payment update - Expense ID: ${stringExpenseId}, Participant: ${stringParticipantId}`);
    
    try {
      // STEP 1: Get the current document from Firestore
      const splitExpenseRef = doc(db, 'splitExpenses', stringExpenseId);
      
      // Print the path to verify we're accessing the correct document
      console.log(`Accessing document at: ${splitExpenseRef.path}`);
      
      const docSnap = await getDoc(splitExpenseRef);
      
      // STEP 2: Verify document exists
      if (!docSnap.exists()) {
        console.error(`Document not found: ${stringExpenseId}`);
        return false;
      }
      
      // STEP 3: Get current data and verify ownership
      const currentData = docSnap.data();
      
      console.log('Current document data:', currentData);
      
      if (currentData.userId !== currentUser.uid) {
        console.error(`User ${currentUser.uid} does not own document ${stringExpenseId}`);
        return false;
      }
      
      // STEP 4: Verify participants array is valid
      if (!Array.isArray(currentData.participants)) {
        console.error('Invalid participants array in document');
        return false;
      }
      
      // STEP 5: Find and update the specific participant
      const participants = [...currentData.participants]; // Create a copy
      const participantIndex = participants.findIndex(p => String(p.id) === stringParticipantId);
      
      if (participantIndex === -1) {
        console.error(`Participant ${stringParticipantId} not found in document`);
        return false;
      }
      
      // STEP 6: Update participant data
      participants[participantIndex] = {
        ...participants[participantIndex],
        status: status,
        paymentMethod: paymentMethod || participants[participantIndex].paymentMethod,
        paidDate: status === 'paid' ? new Date().toISOString() : participants[participantIndex].paidDate
      };
      
      // STEP 7: Calculate new status
      const paidCount = participants.filter(p => p.status === 'paid').length;
      const newStatus = paidCount === 0 ? 'pending' : 
                       paidCount === participants.length ? 'settled' : 'partial';
      
      // STEP 8: Prepare update object with required fields
      const updateData = {
        participants: participants,
        status: newStatus,
        updatedAt: new Date().toISOString()
      };
      
      // This next line is optional but helps verify what we're sending
      console.log('About to update with data:', JSON.stringify(updateData, null, 2));
      
      // STEP 9: Perform the update with explicit try/catch
      try {
        await updateDoc(splitExpenseRef, updateData);
        console.log('Document successfully updated');
        return true;
      } catch (updateError) {
        console.error('Error during updateDoc operation:', updateError);
        console.error('Error code:', updateError.code);
        console.error('Error message:', updateError.message);
        // Log the data being updated
        console.error('Attempted to update with data:', JSON.stringify(updateData, null, 2));
        throw updateError;
      }
    } catch (error) {
      console.error('Error in updatePaymentStatus:', error);
      return false;
    }
  };
  
  /**
   * Add a new contact with improved validation and error handling
   * @param {string} name - Contact name
   * @param {string} email - Contact email
   * @returns {boolean} Success status
   */
  const addContact = async (name, email) => {
    if (!currentUser) {
      console.error('Cannot add contact: No authenticated user');
      return false;
    }
    
    if (!name || name.trim() === '') {
      console.error('Contact name is required');
      return false;
    }
    
    try {
      console.log(`Adding new contact: ${name} (${email || 'No email'})`);
      
      // Check if contact with this email already exists (case insensitive)
      if (email && email.trim() !== '') {
        const normalizedEmail = email.trim().toLowerCase();
        
        // First check against local state to avoid unnecessary Firestore query
        const existingContact = contacts.find(c => 
          c.email && c.email.toLowerCase() === normalizedEmail
        );
        
        if (existingContact) {
          console.log(`Contact with email ${email} already exists`);
          return false;
        }
        
        // Double-check with Firestore to be sure
        const existingContactsQuery = query(
          collection(db, 'contacts'),
          where('userId', '==', currentUser.uid),
          where('email', '==', email.trim())
        );
        
        const existingContacts = await getDocs(existingContactsQuery);
        if (!existingContacts.empty) {
          console.log(`Contact with email ${email} exists in Firestore but not in local state`);
          return false;
        }
      }
      
      // Create new contact object
      const newContactData = {
        name: name.trim(),
        email: email ? email.trim() : '',
        userId: currentUser.uid,
        createdAt: new Date().toISOString()
      };
      
      // Add to Firestore
      const docRef = await addDoc(collection(db, 'contacts'), newContactData);
      console.log('Contact added with ID:', docRef.id);
      
      const newContact = {
        ...newContactData,
        id: String(docRef.id)
      };
      
      // Update local state
      setContacts(prevContacts => [...prevContacts, newContact]);
      
      return true;
    } catch (error) {
      console.error('Error adding contact:', error);
      return false;
    }
  };
  
  /**
   * Delete a contact
   * @param {string} contactId - Contact ID
   * @returns {boolean} Success status
   */
  const deleteContact = async (contactId) => {
    if (!currentUser || !contactId) {
      console.error('Cannot delete contact: Missing user or contact ID');
      return false;
    }
    
    try {
      const stringContactId = String(contactId);
      console.log(`Deleting contact with ID: ${stringContactId}`);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'contacts', stringContactId));
      
      // Update local state
      setContacts(prevContacts => prevContacts.filter(contact => String(contact.id) !== stringContactId));
      
      return true;
    } catch (error) {
      console.error('Error deleting contact:', error);
      return false;
    }
  };
  
  /**
   * Get all split expenses
   * @returns {Array} Array of split expenses
   */
  const getAllSplitExpenses = () => {
    return splitExpenses;
  };
  
  /**
   * Get all contacts
   * @returns {Array} Array of contacts
   */
  const getAllContacts = () => {
    return contacts;
  };
  
  /**
   * Delete a split expense
   * @param {string|number} splitExpenseId - Split expense ID
   * @returns {boolean} Success status
   */
  const deleteSplitExpense = async (splitExpenseId) => {
    if (!currentUser || !splitExpenseId) {
      console.error('Cannot delete split expense: Missing user or split expense ID');
      return false;
    }
    
    try {
      const stringExpenseId = String(splitExpenseId);
      console.log(`Deleting split expense with ID: ${stringExpenseId}`);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'splitExpenses', stringExpenseId));
      
      return true;
    } catch (error) {
      console.error('Error deleting split expense:', error);
      return false;
    }
  };
  
  /**
   * Remind a participant to pay
   * @param {string|number} splitExpenseId - Split expense ID
   * @param {string} participantId - Participant ID
   * @returns {boolean} Success status
   */
  const sendPaymentReminder = async (splitExpenseId, participantId) => {
    if (!currentUser) {
      console.error('Cannot send reminder: No authenticated user');
      return false;
    }
    
    // Always convert IDs to strings for consistent comparison
    const stringExpenseId = String(splitExpenseId);
    const stringParticipantId = String(participantId);
    
    try {
      console.log(`Sending payment reminder for expense=${stringExpenseId}, participant=${stringParticipantId}`);
      
      // Find the split expense in local state
      const splitExpense = splitExpenses.find(expense => 
        String(expense.id) === stringExpenseId
      );
      
      if (!splitExpense) {
        console.error(`Split expense with ID ${stringExpenseId} not found`);
        return false;
      }
      
      // Check if user owns this split expense - important for security rules
      if (splitExpense.userId !== currentUser.uid) {
        console.error('User does not own this split expense');
        return false;
      }
      
      // Find participant
      const participant = splitExpense.participants.find(p => 
        String(p.id) === stringParticipantId
      );
      
      if (!participant) {
        console.error(`Participant with ID ${stringParticipantId} not found`);
        return false;
      }
      
      if (!participant.email) {
        console.error(`Participant ${participant.name || 'Unknown'} has no email address`);
        return false;
      }
      
      // Update email status in UI
      setEmailStatus(prev => ({
        ...prev,
        [stringParticipantId]: { sending: true }
      }));
      
      // Create complete email parameters
      const emailParams = {
        to_email: participant.email,
        to_name: participant.name || 'Participant',
        creator_name: currentUser.name || 'Group member',
        expense_description: splitExpense.description || 'Split expense',
        expense_category: splitExpense.category || 'Uncategorized',
        expense_date: new Date(splitExpense.date || new Date()).toLocaleDateString(),
        expense_amount: (splitExpense.totalAmount || 0).toFixed(2),
        share_amount: (participant.shareAmount || 0).toFixed(2),
        is_reminder: true,
        reminder_message: `This is a friendly reminder that your payment of $${(participant.shareAmount || 0).toFixed(2)} for "${splitExpense.description || 'Split expense'}" is still pending.`,
        // Essential parameters for EmailJS template
        name: currentUser.name || 'Fin-Track',
        email: currentUser.email || '',
        reply_to: currentUser.email || '',
        subject: `Payment Reminder: ${splitExpense.description || 'Split expense'}`
      };
      
      console.log('Sending email with params:', emailParams);
      
      try {
        // Call email service with better error handling
        const result = await sendSplitExpenseEmail(
          participant, 
          splitExpense, 
          currentUser, 
          true // This is a reminder
        );
        
        console.log('Email service result:', result);
        
        // Update email status based on result
        setEmailStatus(prev => ({
          ...prev,
          [stringParticipantId]: { 
            sending: false,
            sent: result.success, 
            error: result.success ? null : (result.message || 'Failed to send email')
          }
        }));
        
        // If successful, log the reminder in Firestore
        if (result.success) {
          try {
            // Create reminder log object with userId for security rules
            const reminderLog = {
              splitExpenseId: stringExpenseId,
              participantId: stringParticipantId,
              timestamp: new Date().toISOString(),
              userId: currentUser.uid, // Critical for security rules
              success: true
            };
            
            // Add to Firestore
            await addDoc(collection(db, 'reminderLogs'), reminderLog);
            console.log('Reminder logged successfully in Firestore');
          } catch (logError) {
            console.error('Error logging reminder:', logError);
            // Continue even if logging fails
          }
        }
        
        return result.success;
      } catch (emailError) {
        console.error('Email service error:', emailError);
        
        // Update email status with error
        setEmailStatus(prev => ({
          ...prev,
          [stringParticipantId]: { 
            sending: false,
            sent: false, 
            error: emailError.message || 'Email service error'
          }
        }));
        
        return false;
      }
    } catch (error) {
      console.error('Error sending payment reminder:', error);
      
      // Update email status in case of error
      if (participantId) {
        setEmailStatus(prev => ({
          ...prev,
          [stringParticipantId]: { 
            sending: false,
            sent: false, 
            error: error.message || 'Unknown error'
          }
        }));
      }
      
      return false;
    }
};
  
  return (
    <SplitExpenseContext.Provider
      value={{
        loading,
        createNewSplitExpense,
        getSplitExpense,
        updatePaymentStatus,
        addContact,
        deleteContact,
        getAllSplitExpenses,
        getAllContacts,
        deleteSplitExpense,
        sendPaymentReminder,
        createParticipant,
        emailStatus
      }}
    >
      {children}
    </SplitExpenseContext.Provider>
  );
};

export default SplitExpenseProvider;